import { toast } from "sonner";
import { Check, Palette } from "lucide-react";
import { Label } from "@/components/ui/label";
import { ColorField } from "@/components/ColorField";
import { api, errText } from "@/lib/api";
import { DEFAULT_NO_FORUM, FORUM_PALETTE, courtColor, readableOn } from "@/lib/forumColors";
import { useForumColors } from "@/context/ForumColorContext";
import { useAuth } from "@/context/AuthContext";

const Swatches = ({ active, onPick, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {FORUM_PALETTE.map((hex) => (
      <button
        key={hex}
        data-testid={`${testId}-${hex.replace("#", "")}`}
        onClick={() => onPick(hex)}
        title={hex}
        className="h-8 w-8 rounded-sm border border-border grid place-items-center transition-transform duration-200 hover:scale-110"
        style={{ background: hex }}
      >
        {active?.toUpperCase() === hex.toUpperCase() ? (
          <Check className="h-4 w-4" style={{ color: readableOn(hex) }} />
        ) : null}
      </button>
    ))}
  </div>
);

export const ForumColorSettings = () => {
  const { isOwner, user, savePreferences } = useAuth();
  const { courts, refresh } = useForumColors();
  const noForum = user?.preferences?.no_forum_color || DEFAULT_NO_FORUM;

  const saveCourt = async (court, color) => {
    try {
      await api.put(`/courts/${court.court_id}`, { ...court, color });
      toast.success(`${court.name} colour updated`);
      await refresh();
      window.dispatchEvent(new Event("forum-colors-changed"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveNoForum = async (color) => {
    try {
      await savePreferences({ ...(user?.preferences || {}), no_forum_color: color });
      toast.success("Colour for entries without a forum updated");
      window.dispatchEvent(new Event("forum-colors-changed"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <section className="surface p-6 lg:p-8" data-testid="forum-colors-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <Palette className="h-5 w-5" /> Forum colours
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Every listing, timeline block and pie slice takes the colour of its forum, so the week reads
        at a glance. Pick from the bold palette, tap the swatch for the full colour spectrum, or type
        an exact hex.
      </p>

      <div className="mt-6 space-y-6">
        {courts.map((court, i) => {
          const color = courtColor(court, i);
          return (
            <div
              key={court.court_id}
              data-testid={`forum-color-${court.court_id}`}
              className="border border-border rounded-sm p-4 flex flex-col gap-3"
              style={{ borderLeft: `6px solid ${color}` }}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-semibold truncate">{court.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{color.toUpperCase()}</p>
                </div>
                {isOwner ? (
                  <ColorField
                    value={color}
                    testId={`forum-hex-${court.court_id}`}
                    onApply={(hex) => saveCourt(court, hex)}
                  />
                ) : null}
              </div>
              {isOwner ? (
                <Swatches
                  active={color}
                  testId={`forum-swatch-${court.court_id}`}
                  onPick={(hex) => saveCourt(court, hex)}
                />
              ) : null}
            </div>
          );
        })}
        {courts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Add a court profile first.</p>
        ) : null}

        <div
          data-testid="forum-color-no-forum"
          className="border border-border rounded-sm p-4 flex flex-col gap-3"
          style={{ borderLeft: `6px solid ${noForum}` }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Label className="text-base font-semibold">No forum · post-court · unassigned</Label>
              <p className="text-xs text-muted-foreground font-mono">{noForum.toUpperCase()}</p>
            </div>
            <ColorField value={noForum} testId="forum-hex-no-forum" onApply={saveNoForum} />
          </div>
          <Swatches active={noForum} testId="forum-swatch-no-forum" onPick={saveNoForum} />
          <p className="text-sm text-muted-foreground">
            Entries with no forum use this. You can still give a single entry its own colour while
            adding it.
          </p>
        </div>
      </div>
    </section>
  );
};
